import {
  AdditiveBlending,
  BoxGeometry,
  ClampToEdgeWrapping,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { createCanvas, finishTexture } from './theme.js';
import { Parts } from './parts.js';
import { addStaticCollider } from './util.js';

/**
 * ٥٠ فاكهة — the red-fronted fruit-juice shop near the north end of Street 60.
 *
 * The reference is a tall red-panelled shopfront with a full glazed front,
 * white "50" numerals and the Arabic name picked out in raised white letters.
 * It sits on the west pavement, opposite the fuel stations, just short of the
 * Al-Mashtal checkpoint.
 */

export const JUICE_SHOP_SITE = Object.freeze({
  id: 'fifty-fruit',
  s: 5170,
  side: -1,
  length: 18,
  depth: 7.8,
  frontLateral: 14.55,
  exclusionStart: 5156,
  exclusionEnd: 5184,
});

const LENGTH = JUICE_SHOP_SITE.length;
const DEPTH = JUICE_SHOP_SITE.depth;
const HALF_LENGTH = LENGTH / 2;
const FRONT = -JUICE_SHOP_SITE.frontLateral;
const CENTER = FRONT - DEPTH / 2;
const BACK = FRONT - DEPTH;
const HEIGHT = 6.05;
const GLASS_BOTTOM = 0.32;
const GLASS_TOP = 3.18;
const GLASS_HEIGHT = GLASS_TOP - GLASS_BOTTOM;
const BAYS = 5;

const COLOR = {
  red: '#c62f27',
  redDark: '#8e1d1a',
  redShadow: '#6f1715',
  orange: '#e07a22',
  gold: '#f0a83c',
  cream: '#f2e7cf',
  warmWhite: '#f7f1e3',
  tile: '#d9cbb5',
  tileAlt: '#cfbea4',
  grout: '#b6a78f',
  steel: '#c9c7bd',
  steelDark: '#5b5d5a',
  charcoal: '#282a28',
  wood: '#8a5c35',
  woodDark: '#674126',
  green: '#3f6b45',
  leaf: '#557c4c',
  skin: '#795239',
  apron: '#e8dfca',
  shirt: '#efeee7',
  teal: '#3e6b6a',
  fridge: '#ece9df',
  juiceOrange: '#e88a25',
  juiceMango: '#e7b52e',
  juiceGuava: '#a34b3d',
};

const ARABIC_FONT = '"Geeza Pro", "Al Nile", "Noto Naskh Arabic", "Times New Roman", serif';
const LATIN_FONT = '"Avenir Next", "Helvetica Neue", Arial, sans-serif';

export function buildJuiceShop(level, route) {
  const site = JUICE_SHOP_SITE;
  const frame = route.frameAt(site.s);
  const group = new Group();
  group.position.copy(frame.position);
  group.rotation.y = frame.yaw;
  group.name = 'juice-shop-50-fakiha';
  level.root.add(group);

  const structure = new Parts();
  buildShell(structure);
  buildExterior(structure);

  const interior = new Parts();
  buildInterior(interior);
  buildFigures(interior, structure);

  const shell = new Mesh(
    structure.merge(),
    new MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.62,
      metalness: 0.08,
    }),
  );
  shell.castShadow = true;
  shell.receiveShadow = true;
  shell.name = 'juice-shop-shell';
  group.add(shell);

  const interiorMaterial = new MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.78,
    metalness: 0.01,
    emissive: 0xffb361,
    emissiveIntensity: 0.06,
  });
  const interiorMesh = new Mesh(interior.merge(), interiorMaterial);
  interiorMesh.castShadow = true;
  interiorMesh.receiveShadow = true;
  interiorMesh.name = 'juice-shop-interior';
  group.add(interiorMesh);

  const glass = new Mesh(
    buildGlassGeometry(),
    new MeshStandardMaterial({
      color: 0x1a2a2c,
      roughness: 0.12,
      metalness: 0.45,
      transparent: true,
      opacity: 0.26,
      depthWrite: false,
      side: DoubleSide,
    }),
  );
  glass.name = 'juice-shop-glass';
  group.add(glass);

  const floorMaterial = new MeshStandardMaterial({
    map: createFloorTexture(),
    roughness: 0.52,
    metalness: 0.03,
  });
  const floor = new Mesh(new PlaneGeometry(DEPTH - 0.5, LENGTH - 0.5), floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(CENTER, 0.095, 0);
  floor.receiveShadow = true;
  floor.name = 'juice-shop-floor';
  group.add(floor);

  const glowMaterial = new MeshStandardMaterial({
    color: 0xfff3d7,
    emissive: 0xffcf82,
    emissiveIntensity: 0.52,
    roughness: 0.48,
    metalness: 0.01,
  });
  const glow = new Mesh(buildGlowGeometry(), glowMaterial);
  glow.name = 'juice-shop-interior-lights';
  group.add(glow);

  const signMaterial = new MeshBasicMaterial({
    map: createSignTexture(),
    transparent: true,
    depthWrite: false,
    toneMapped: false,
  });
  const sign = new Mesh(new PlaneGeometry(14.3, 2.8), signMaterial);
  sign.position.set(FRONT + 0.06, 4.55, 0);
  sign.rotation.y = Math.PI / 2;
  sign.name = 'juice-shop-sign';
  group.add(sign);

  const plaqueMaterial = new MeshBasicMaterial({
    map: createPlaqueTexture(),
    toneMapped: false,
  });
  const plaque = new Mesh(new PlaneGeometry(2.25, 0.72), plaqueMaterial);
  plaque.position.set(FRONT + 0.065, 2.82, 6.2);
  plaque.rotation.y = Math.PI / 2;
  plaque.name = 'juice-shop-plaque';
  group.add(plaque);

  const menuMaterial = new MeshBasicMaterial({
    map: createMenuTexture(),
    toneMapped: false,
    side: DoubleSide,
  });
  const menu = new Mesh(new PlaneGeometry(2.4, 1.05), menuMaterial);
  menu.position.set(BACK + 0.36, 2.32, 3.85);
  menu.rotation.y = Math.PI / 2;
  menu.name = 'juice-shop-menu';
  group.add(menu);

  const boardMaterial = new MeshBasicMaterial({
    map: createPavementMenuTexture(),
    toneMapped: false,
    side: DoubleSide,
  });
  const board = new Mesh(new PlaneGeometry(0.72, 0.94), boardMaterial);
  board.position.set(-13.1, 0.78, 4.25);
  board.rotation.y = Math.PI / 2;
  board.rotation.z = -0.08;
  board.name = 'juice-shop-pavement-board';
  group.add(board);

  const glowSpillMaterial = new MeshBasicMaterial({
    map: createSpillTexture(),
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: AdditiveBlending,
    toneMapped: false,
    side: DoubleSide,
  });
  const spill = new Mesh(new PlaneGeometry(6.8, 12.5), glowSpillMaterial);
  spill.rotation.x = -Math.PI / 2;
  spill.position.set(-12.3, 0.08, 0);
  spill.renderOrder = 2;
  spill.name = 'juice-shop-light-spill';
  group.add(spill);

  const centre = route.offsetFrame(site.s, -site.frontLateral - DEPTH / 2);
  addStaticCollider(level, {
    x: centre.position.x,
    y: HEIGHT / 2,
    z: centre.position.z,
    width: DEPTH,
    height: HEIGHT,
    depth: LENGTH,
    yaw: frame.yaw,
  });

  // The pavement board is close enough to the kerb to be worth a small box
  // collider, so the Hilux cannot drive through it.
  const boardPoint = route.offsetFrame(site.s, -13.15).position
    .clone()
    .addScaledVector(frame.tangent, -4.25);
  addStaticCollider(level, {
    x: boardPoint.x,
    y: 0.7,
    z: boardPoint.z,
    width: 0.9,
    height: 1.2,
    depth: 1.15,
    yaw: frame.yaw,
  });

  // Where the shop is joined, measured on the pavement in front of the door.
  // The radius in config.juice is large enough to cover the whole frontage from
  // the southbound slow lane without reaching across the median.
  const position = route
    .offsetFrame(site.s, -site.frontLateral + 2.2)
    .position.clone();

  return {
    site,
    name: '٥٠ فاكهة',
    group,
    position,
    purchases: 0,
    update(lit = 0) {
      glowMaterial.emissiveIntensity = 0.42 + lit * 1.4;
      glowSpillMaterial.opacity = lit * 0.24;
      interiorMaterial.emissiveIntensity = 0.04 + lit * 0.32;
    },
  };
}

function buildShell(parts) {
  const glassMid = (GLASS_BOTTOM + GLASS_TOP) / 2;

  // Back and side walls. The front is deliberately left open for the glazing.
  parts.box(0.26, HEIGHT, LENGTH, COLOR.redDark, BACK + 0.13, HEIGHT / 2, 0);
  parts.box(DEPTH, HEIGHT, 0.26, COLOR.red, CENTER, HEIGHT / 2, -HALF_LENGTH + 0.13);
  parts.box(DEPTH, HEIGHT, 0.26, COLOR.red, CENTER, HEIGHT / 2, HALF_LENGTH - 0.13);

  // Roof slab and parapet cap.
  parts.box(DEPTH + 0.5, 0.24, LENGTH + 0.5, COLOR.redDark, CENTER, HEIGHT + 0.12, 0);
  parts.box(DEPTH + 0.74, 0.14, LENGTH + 0.74, COLOR.charcoal, CENTER, HEIGHT + 0.3, 0);

  // Red cladding above the shopfront.
  const upperHeight = HEIGHT - GLASS_TOP;
  parts.box(0.28, upperHeight, LENGTH, COLOR.red, FRONT - 0.14, GLASS_TOP + upperHeight / 2, 0);

  // Horizontal panel joints and vertical seams, drawn as shallow raised strips.
  for (const y of [3.66, 4.29, 4.92, 5.55]) {
    parts.box(0.045, 0.045, LENGTH - 0.1, COLOR.redShadow, FRONT + 0.025, y, 0);
  }
  for (let z = -HALF_LENGTH + 2.25; z < HALF_LENGTH; z += 2.25) {
    parts.box(
      0.045,
      upperHeight - 0.12,
      0.035,
      COLOR.redShadow,
      FRONT + 0.025,
      GLASS_TOP + upperHeight / 2,
      z,
    );
  }

  // Shopfront framing.
  parts.box(0.22, 0.26, LENGTH, COLOR.steel, FRONT - 0.02, GLASS_TOP + 0.11, 0);
  parts.box(0.28, 0.34, LENGTH, COLOR.steelDark, FRONT - 0.02, 0.17, 0);
  for (let i = 0; i <= BAYS; i += 1) {
    const z = -HALF_LENGTH + (LENGTH / BAYS) * i;
    parts.box(
      0.2,
      GLASS_HEIGHT + 0.14,
      i === 0 || i === BAYS ? 0.24 : 0.16,
      COLOR.steel,
      FRONT - 0.01,
      glassMid,
      z,
    );
  }

  // The double door sits in the middle bay: handles, lock rail and a mat.
  for (const z of [-0.34, 0.34]) {
    parts.box(0.05, 1.12, 0.05, COLOR.steelDark, FRONT + 0.075, 1.42, z);
  }
  parts.box(0.05, 0.08, 0.82, COLOR.steelDark, FRONT + 0.06, 1.98, 0);
  parts.box(0.95, 0.035, 1.25, COLOR.charcoal, FRONT + 0.19, 0.075, 0);

  // A slim white fascia over the shopfront, with the top cap over it.
  parts.box(0.34, 0.1, LENGTH + 0.1, COLOR.cream, FRONT - 0.05, GLASS_TOP + 0.28, 0);
  parts.box(0.34, 0.12, LENGTH + 0.1, COLOR.cream, FRONT - 0.05, HEIGHT - 0.03, 0);

  // Downpipes at the corners, and small bollards either side of the frontage.
  for (const z of [-HALF_LENGTH + 0.35, HALF_LENGTH - 0.35]) {
    parts.cyl(0.055, 0.055, HEIGHT - 0.1, 8, COLOR.steelDark, FRONT + 0.08, HEIGHT / 2, z);
  }
  for (const z of [-8.25, 8.25]) {
    parts.cyl(0.11, 0.13, 0.72, 10, COLOR.charcoal, -13.75, 0.36, z);
    parts.cyl(0.13, 0.13, 0.06, 10, COLOR.steel, -13.75, 0.74, z);
  }
}

function buildInterior(parts) {
  // Warm wall and ceiling liners, kept a little proud of the structure.
  parts.box(0.06, HEIGHT - 0.36, LENGTH - 0.6, COLOR.warmWhite, BACK + 0.3, HEIGHT / 2, 0);
  parts.box(DEPTH - 0.5, HEIGHT - 0.36, 0.06, COLOR.cream, CENTER, HEIGHT / 2, -HALF_LENGTH + 0.3);
  parts.box(DEPTH - 0.5, HEIGHT - 0.36, 0.06, COLOR.cream, CENTER, HEIGHT / 2, HALF_LENGTH - 0.3);
  parts.box(DEPTH - 0.35, 0.07, LENGTH - 0.35, COLOR.warmWhite, CENTER, HEIGHT - 0.12, 0);

  // Counter along the back of the shop.
  const counterX = -20.55;
  const counterZ = 0.4;
  parts.box(1.05, 1.04, 5.5, COLOR.cream, counterX, 0.62, counterZ);
  parts.box(1.18, 0.08, 5.68, COLOR.charcoal, counterX, 1.2, counterZ);
  parts.box(0.92, 0.12, 5.18, COLOR.wood, counterX, 0.19, counterZ);

  // Juice dispensers and a stack of cups.
  for (const [z, colour] of [
    [counterZ - 1.5, COLOR.juiceOrange],
    [counterZ, COLOR.juiceMango],
    [counterZ + 1.5, COLOR.juiceGuava],
  ]) {
    parts.box(0.48, 0.55, 0.48, COLOR.steel, counterX, 1.5, z);
    parts.box(0.4, 0.3, 0.4, colour, counterX, 1.8, z);
    parts.cyl(0.09, 0.11, 0.2, 10, COLOR.cream, counterX, 1.63, z + 0.26);
  }
  for (let i = 0; i < 4; i += 1) {
    parts.cyl(0.13, 0.16, 0.16, 10, COLOR.warmWhite, counterX + 0.02, 1.36 + i * 0.055, counterZ + 2.25);
  }

  // Shelving and crates behind the counter.
  for (const y of [2.35, 3.15]) {
    parts.box(0.46, 0.07, 5.4, COLOR.wood, BACK + 0.72, y, 0.35);
  }
  const crateColors = [COLOR.juiceOrange, COLOR.juiceMango, COLOR.green, COLOR.juiceGuava];
  for (let i = 0; i < 7; i += 1) {
    const z = -2.15 + i * 0.72;
    for (let row = 0; row < 2; row += 1) {
      parts.box(0.32, 0.34, 0.58, COLOR.woodDark, BACK + 0.75, 2.58 + row * 0.8, z);
      parts.sphere(
        0.13,
        7,
        5,
        crateColors[(i + row) % crateColors.length],
        BACK + 0.58,
        2.8 + row * 0.8,
        z - 0.16,
      );
      parts.sphere(
        0.12,
        7,
        5,
        crateColors[(i + row + 1) % crateColors.length],
        BACK + 0.58,
        2.8 + row * 0.8,
        z + 0.16,
      );
    }
  }

  // Display fridge on the north side. The body is a frame rather than a solid
  // box so its softly lit shelves stay visible through the glass door.
  const fridgeX = -20.55;
  const fridgeZ = 6.25;
  parts.box(1.65, 0.1, 2.35, COLOR.fridge, fridgeX, 2.5, fridgeZ);
  parts.box(1.65, 0.1, 2.35, COLOR.fridge, fridgeX, 0.12, fridgeZ);
  parts.box(0.1, 2.4, 2.35, COLOR.fridge, fridgeX - 0.78, 1.3, fridgeZ);
  for (const z of [fridgeZ - 1.13, fridgeZ + 1.13]) {
    parts.box(1.65, 2.4, 0.1, COLOR.fridge, fridgeX, 1.3, z);
  }
  parts.box(1.7, 0.08, 2.4, COLOR.steelDark, fridgeX, 2.46, fridgeZ);
  for (const y of [0.72, 1.35, 1.98]) {
    parts.box(0.7, 0.05, 1.9, COLOR.steel, fridgeX + 0.42, y, fridgeZ);
  }
  for (const z of [fridgeZ - 0.45, fridgeZ + 0.45]) {
    parts.box(0.05, 0.65, 0.05, COLOR.steelDark, fridgeX + 0.9, 1.48, z);
  }
  for (let row = 0; row < 3; row += 1) {
    for (let i = 0; i < 3; i += 1) {
      parts.cyl(
        0.07,
        0.09,
        0.25,
        8,
        i % 2 ? COLOR.juiceMango : COLOR.juiceOrange,
        fridgeX + 0.44,
        0.85 + row * 0.63,
        fridgeZ - 0.55 + i * 0.55,
      );
    }
  }

  // Produce table on the south side, with crates of fruit and a melon.
  const tableZ = -6.1;
  parts.box(1.9, 0.16, 2.8, COLOR.wood, -20.25, 0.85, tableZ);
  for (const x of [-20.9, -19.6]) {
    for (const z of [tableZ - 1.1, tableZ + 1.1]) {
      parts.box(0.08, 0.78, 0.08, COLOR.woodDark, x, 0.42, z);
    }
  }
  for (let i = 0; i < 5; i += 1) {
    const z = tableZ - 1.05 + i * 0.5;
    parts.box(0.72, 0.24, 0.42, COLOR.woodDark, -20.25, 0.98, z);
    parts.sphere(0.12, 7, 5, crateColors[i % crateColors.length], -20.25, 1.15, z);
    parts.sphere(0.1, 7, 5, crateColors[(i + 2) % crateColors.length], -20.25, 1.16, z + 0.12);
  }
  parts.sphere(0.24, 9, 6, COLOR.green, -19.7, 1.14, tableZ + 0.95);

  // Low display plinths near the windows.
  for (const z of [-4.4, 4.1]) {
    parts.box(1.3, 0.5, 1.5, COLOR.woodDark, -16.3, 0.3, z);
    for (let i = 0; i < 3; i += 1) {
      parts.sphere(
        0.16,
        8,
        5,
        crateColors[(i + (z > 0 ? 1 : 0)) % crateColors.length],
        -16.3,
        0.68,
        z - 0.4 + i * 0.4,
      );
    }
  }

  // Wall clock and a little foliage by the entrance.
  parts.cyl(0.28, 0.28, 0.04, 16, COLOR.cream, BACK + 0.36, 2.9, -5.2, { z: Math.PI / 2 });
  parts.cyl(0.23, 0.23, 0.05, 16, COLOR.charcoal, BACK + 0.33, 2.9, -5.2, { z: Math.PI / 2 });
  for (const z of [-8.1, 7.9]) {
    parts.cyl(0.24, 0.2, 0.45, 10, COLOR.woodDark, -14.95, 0.3, z);
    for (let i = 0; i < 4; i += 1) {
      parts.sphere(
        0.22,
        7,
        5,
        COLOR.leaf,
        -14.95 + (i % 2 ? 0.13 : -0.13),
        0.72 + i * 0.18,
        z + (i > 1 ? 0.12 : -0.12),
      );
    }
  }
}

function buildExterior(parts) {
  // Pavement A-frame: timber legs and a crossbar. The menu face is a separate
  // textured plane so its type stays crisp.
  for (const z of [3.84, 4.66]) {
    parts.box(0.06, 1.2, 0.06, COLOR.woodDark, -13.15, 0.6, z);
  }
  parts.box(0.06, 0.06, 0.92, COLOR.woodDark, -13.15, 1.15, 4.25);
  parts.box(0.06, 0.06, 0.92, COLOR.woodDark, -13.15, 0.32, 4.25);

  // Crates outside, stacked where the delivery left them.
  for (let i = 0; i < 3; i += 1) {
    const z = -4.15 - i * 0.37;
    parts.box(0.74, 0.42, 0.62, COLOR.woodDark, -13.28, 0.25 + i * 0.02, z);
    parts.sphere(0.13, 7, 5, i === 1 ? COLOR.juiceMango : COLOR.juiceOrange, -13.52, 0.52, z - 0.16);
    parts.sphere(0.12, 7, 5, i === 2 ? COLOR.green : COLOR.juiceGuava, -13.08, 0.52, z + 0.16);
  }

  // A low planter between the shop and the neighbouring wall.
  parts.box(1.0, 0.5, 1.0, COLOR.woodDark, -14.0, 0.25, -7.9);
  for (let i = 0; i < 4; i += 1) {
    parts.sphere(
      0.2,
      7,
      5,
      COLOR.leaf,
      -14.0 + (i % 2 ? 0.15 : -0.15),
      0.58 + (i > 1 ? 0.14 : 0),
      -7.9 + (i > 1 ? 0.14 : -0.14),
    );
  }

  // A thin lintel over the pavement board, matching the shop's red trim.
  parts.box(0.12, 0.08, 1.12, COLOR.red, -13.15, 1.36, 4.25);
}

function buildFigures(interior, exterior) {
  // The person behind the counter, in a white shirt and apron.
  const x = -21.55;
  const z = 1.05;
  interior.cyl(0.19, 0.27, 1.12, 10, COLOR.shirt, x, 0.97, z);
  interior.box(0.38, 0.66, 0.06, COLOR.apron, x + 0.2, 0.9, z);
  interior.sphere(0.145, 9, 7, COLOR.skin, x, 1.67, z);
  interior.sphere(0.15, 9, 7, COLOR.charcoal, x, 1.76, z);
  interior.box(0.1, 0.08, 0.32, COLOR.skin, x + 0.14, 1.36, z);

  // A customer on the pavement, looking at the board.
  const px = -13.65;
  const pz = 2.4;
  exterior.cyl(0.21, 0.3, 1.4, 10, COLOR.teal, px, 0.76, pz);
  exterior.sphere(0.14, 9, 7, COLOR.skin, px, 1.57, pz);
  exterior.sphere(0.15, 9, 7, COLOR.cream, px, 1.66, pz);
  exterior.cyl(0.025, 0.025, 0.78, 6, COLOR.skin, px + 0.05, 0.93, pz - 0.34);
}

function buildGlassGeometry() {
  const geometries = [];
  const bay = LENGTH / BAYS;
  for (let i = 0; i < BAYS; i += 1) {
    const pane = new BoxGeometry(0.035, GLASS_HEIGHT, bay - 0.28);
    pane.translate(FRONT - 0.02, (GLASS_BOTTOM + GLASS_TOP) / 2, -HALF_LENGTH + bay * (i + 0.5));
    geometries.push(pane);
  }

  const fridgeDoor = new BoxGeometry(0.035, 1.9, 1.95);
  fridgeDoor.translate(-20.55 + 0.825, 1.35, 6.25);
  geometries.push(fridgeDoor);

  const merged = mergeGeometries(geometries, false);
  for (const geometry of geometries) geometry.dispose();
  return merged;
}

function buildGlowGeometry() {
  const geometries = [];
  for (const x of [-17.45, -19.75]) {
    for (const z of [-5.6, 0, 5.6]) {
      const panel = new BoxGeometry(1.25, 0.06, 2.1);
      panel.translate(x, 5.76, z);
      geometries.push(panel);
    }
  }

  const fridgeBack = new BoxGeometry(0.035, 1.92, 1.96);
  fridgeBack.translate(-21.24, 1.35, 6.25);
  geometries.push(fridgeBack);

  const merged = mergeGeometries(geometries, false);
  for (const geometry of geometries) geometry.dispose();
  return merged;
}

function createSignTexture() {
  const canvas = createCanvas(2048, 400);
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, 2048, 400);

  drawRaisedText(ctx, 'فاكهة', 430, 198, `700 238px ${ARABIC_FONT}`, 'rtl');
  drawJuiceCup(ctx, 1000, 190, 1.0);
  drawRaisedText(ctx, '50', 1580, 178, `900 286px ${LATIN_FONT}`, 'ltr');

  // A small manufacturer-style line under the name, as on the real fascia.
  ctx.save();
  ctx.fillStyle = '#f3c46e';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = 'rtl';
  ctx.font = `600 36px ${ARABIC_FONT}`;
  ctx.fillText('عصير طازج', 430, 345);
  ctx.direction = 'ltr';
  ctx.font = `600 26px ${LATIN_FONT}`;
  ctx.fillText('FRESH JUICE', 1580, 342);
  ctx.restore();

  const texture = finishTexture(canvas, 8);
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function createPlaqueTexture() {
  const canvas = createCanvas(640, 220);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#f6eedb';
  ctx.fillRect(0, 0, 640, 220);
  ctx.strokeStyle = '#b32620';
  ctx.lineWidth = 7;
  ctx.strokeRect(8, 8, 624, 204);

  ctx.fillStyle = '#a5221d';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = 'rtl';
  ctx.font = `700 82px ${ARABIC_FONT}`;
  ctx.fillText('٥٠ فاكهة', 320, 92);

  ctx.fillStyle = '#3d3b35';
  ctx.direction = 'ltr';
  ctx.font = `600 25px ${LATIN_FONT}`;
  ctx.fillText('FRESH FRUIT JUICE', 320, 166);

  const texture = finishTexture(canvas, 8);
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function createMenuTexture() {
  const canvas = createCanvas(1024, 448);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#f7efe0';
  ctx.fillRect(0, 0, 1024, 448);
  ctx.strokeStyle = '#b32620';
  ctx.lineWidth = 12;
  ctx.strokeRect(16, 16, 992, 416);

  ctx.fillStyle = '#a5221d';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = 'rtl';
  ctx.font = `700 86px ${ARABIC_FONT}`;
  ctx.fillText('عصير طازج', 512, 96);

  ctx.fillStyle = '#3d3b35';
  ctx.direction = 'ltr';
  ctx.font = `600 26px ${LATIN_FONT}`;
  ctx.fillText('FRESH JUICE', 512, 154);

  const fruits = [
    ['#e88a25', 300, 250, 40],
    ['#e7b52e', 512, 238, 43],
    ['#a34b3d', 724, 250, 40],
  ];
  for (const [colour, x, y, r] of fruits) {
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#557c4c';
    ctx.beginPath();
    ctx.ellipse(x + 12, y - r - 6, 18, 8, -0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = '#3d3b35';
  ctx.direction = 'rtl';
  ctx.font = `600 42px ${ARABIC_FONT}`;
  ctx.fillText('مانجو · برتقال · جوافة · ليمون', 512, 350);

  ctx.fillStyle = '#b32620';
  ctx.font = `700 42px ${ARABIC_FONT}`;
  ctx.fillText('٥٠ فاكهة', 512, 402);

  const texture = finishTexture(canvas, 8);
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function createPavementMenuTexture() {
  const canvas = createCanvas(512, 672);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#f7efe0';
  ctx.fillRect(0, 0, 512, 672);
  ctx.strokeStyle = '#b32620';
  ctx.lineWidth = 11;
  ctx.strokeRect(13, 13, 486, 646);

  ctx.fillStyle = '#a5221d';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = 'rtl';
  ctx.font = `700 62px ${ARABIC_FONT}`;
  ctx.fillText('عصير طازج', 256, 96);

  ctx.fillStyle = '#3d3b35';
  ctx.direction = 'ltr';
  ctx.font = `600 23px ${LATIN_FONT}`;
  ctx.fillText('FRESH JUICE', 256, 140);

  for (const [colour, x, y] of [
    ['#e88a25', 128, 222],
    ['#e7b52e', 256, 212],
    ['#a34b3d', 384, 222],
  ]) {
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.arc(x, y, 38, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#557c4c';
    ctx.beginPath();
    ctx.ellipse(x + 10, y - 46, 17, 7, -0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = '#3d3b35';
  ctx.direction = 'rtl';
  ctx.font = `600 33px ${ARABIC_FONT}`;
  for (const [line, y] of [
    ['مانجو', 330],
    ['برتقال', 390],
    ['جوافة', 450],
    ['ليمون', 510],
  ]) {
    ctx.fillText(line, 256, y);
  }

  ctx.fillStyle = '#b32620';
  ctx.font = `700 40px ${ARABIC_FONT}`;
  ctx.fillText('٥٠ فاكهة', 256, 610);

  const texture = finishTexture(canvas, 8);
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function createFloorTexture() {
  const canvas = createCanvas(512, 512);
  const ctx = canvas.getContext('2d');
  const tile = 128;
  for (let y = 0; y < 4; y += 1) {
    for (let x = 0; x < 4; x += 1) {
      ctx.fillStyle = (x + y) % 2 === 0 ? COLOR.tile : COLOR.tileAlt;
      ctx.fillRect(x * tile, y * tile, tile, tile);
    }
  }
  ctx.strokeStyle = COLOR.grout;
  ctx.lineWidth = 4;
  for (let i = 0; i <= 4; i += 1) {
    ctx.beginPath();
    ctx.moveTo(i * tile, 0);
    ctx.lineTo(i * tile, 512);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, i * tile);
    ctx.lineTo(512, i * tile);
    ctx.stroke();
  }
  // A few flecks so the floor does not read as a flat checkerboard.
  ctx.fillStyle = 'rgba(90, 75, 58, 0.13)';
  for (let i = 0; i < 160; i += 1) {
    const x = (i * 97) % 512;
    const y = (i * 53) % 512;
    ctx.fillRect(x, y, 2 + (i % 3), 2);
  }
  const texture = finishTexture(canvas, 8);
  texture.repeat.set(2, 5);
  texture.needsUpdate = true;
  return texture;
}

function createSpillTexture() {
  const canvas = createCanvas(256, 256);
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(128, 128, 4, 128, 128, 128);
  gradient.addColorStop(0, 'rgba(255, 214, 145, 0.62)');
  gradient.addColorStop(0.45, 'rgba(255, 190, 108, 0.3)');
  gradient.addColorStop(1, 'rgba(255, 170, 80, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 256, 256);
  const texture = finishTexture(canvas, 4);
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function drawRaisedText(ctx, text, x, y, font, direction = 'ltr') {
  ctx.save();
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = direction;
  ctx.fillStyle = '#6f1715';
  ctx.fillText(text, x + 14, y + 18);
  ctx.fillStyle = COLOR.orange;
  ctx.fillText(text, x + 8, y + 10);
  ctx.strokeStyle = COLOR.orange;
  ctx.lineWidth = 8;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = '#f7f1e3';
  ctx.fillText(text, x, y);
  ctx.restore();
}

function drawJuiceCup(ctx, cx, cy, scale) {
  const cup = (offsetX, offsetY) => {
    ctx.beginPath();
    ctx.moveTo(-56 + offsetX, -84 + offsetY);
    ctx.lineTo(56 + offsetX, -84 + offsetY);
    ctx.lineTo(40 + offsetX, 78 + offsetY);
    ctx.quadraticCurveTo(0 + offsetX, 100 + offsetY, -40 + offsetX, 78 + offsetY);
    ctx.closePath();
  };

  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);
  ctx.lineJoin = 'round';

  cup(8, 10);
  ctx.fillStyle = '#6f1715';
  ctx.fill();
  cup(4, 5);
  ctx.fillStyle = COLOR.orange;
  ctx.fill();
  cup(0, 0);
  ctx.fillStyle = '#f7f1e3';
  ctx.fill();
  ctx.strokeStyle = COLOR.orange;
  ctx.lineWidth = 5;
  ctx.stroke();

  // Juice level and a straw.
  ctx.beginPath();
  ctx.moveTo(-46, 8);
  ctx.lineTo(46, 8);
  ctx.lineTo(38, 60);
  ctx.quadraticCurveTo(0, 78, -38, 60);
  ctx.closePath();
  ctx.fillStyle = COLOR.gold;
  ctx.fill();

  ctx.strokeStyle = '#f7f1e3';
  ctx.lineWidth = 10;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(18, -80);
  ctx.lineTo(40, -150);
  ctx.stroke();
  ctx.strokeStyle = COLOR.orange;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(18, -80);
  ctx.lineTo(40, -150);
  ctx.stroke();

  // A little orange slice on the rim.
  ctx.fillStyle = COLOR.orange;
  ctx.beginPath();
  ctx.arc(-46, -72, 24, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#f7f1e3';
  ctx.beginPath();
  ctx.arc(-46, -72, 15, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
